import { useCallback, useEffect, useRef } from 'react';

export type InfiniteScrollTriggerProps = {
  /** True while another page exists on the server. Nothing is rendered once false. */
  hasMore: boolean;
  /** True while the next page is being fetched; blocks duplicate requests. */
  isLoading: boolean;
  /** True when the last page request failed: automatic loading stops and the button turns into a retry. */
  isError?: boolean;
  onLoadMore: () => void;
  /** Visible fallback button label, for keyboard users and browsers without IntersectionObserver. */
  label?: string;
  /** Button label after a failed request. */
  errorLabel?: string;
  /** Distance before the end of the list at which the next page starts loading. */
  rootMargin?: string;
  'data-testid'?: string;
};

export const DEFAULT_ROOT_MARGIN = '200px';

/**
 * Calls `onLoadMore` as soon as the sentinel scrolls into view (with a margin so the next page
 * arrives before the user reaches the bottom). Never fires while a page loads or once the list is complete.
 */
export function useInfiniteScrollTrigger(props: InfiniteScrollTriggerProps) {
  const nodeRef = useRef<HTMLElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const canLoadRef = useRef(false);
  const onLoadMoreRef = useRef(props.onLoadMore);

  canLoadRef.current = props.hasMore && !props.isLoading && !props.isError;
  onLoadMoreRef.current = props.onLoadMore;

  const sentinelRef = useCallback((node: HTMLElement | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    nodeRef.current = node;

    if (!node || typeof IntersectionObserver === 'undefined') {
      return;
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting) && canLoadRef.current) {
          onLoadMoreRef.current();
        }
      },
      { rootMargin: props.rootMargin ?? DEFAULT_ROOT_MARGIN },
    );
    observerRef.current.observe(node);
  }, [props.rootMargin]);

  // A page too short to fill the viewport leaves the sentinel visible, and the observer only reports
  // changes. Re-observing once loading settles yields a fresh entry, which chains the next page.
  // Skipped after a failure, otherwise a visible sentinel would retry in a tight loop.
  useEffect(() => {
    const observer = observerRef.current;
    const node = nodeRef.current;
    if (!observer || !node || props.isLoading || !props.hasMore || props.isError) {
      return;
    }
    observer.unobserve(node);
    observer.observe(node);
  }, [props.isLoading, props.hasMore, props.isError]);

  useEffect(() => () => observerRef.current?.disconnect(), []);

  return {
    sentinelRef,
    label: props.isError ? props.errorLabel ?? 'Réessayer' : props.label ?? 'Voir plus',
  };
}
